import {
  getOscConfig,
  getTwilioHost,
  getTwilioProtocol,
  getRealm,
  Hostname,
} from "@twiliointernal/e2-commons-shared-definitions";
import {
  baseUrl,
  API_ENDPOINTS,
  CONSOLE_ZEN_REDIRECT_ROOT_PATH,
} from "./definitions/constants";
import { logger } from "@twiliointernal/e2-commons-shared-libs";
import { PostAuthRedirectInput } from "./definitions/types";

const { hostName } = getOscConfig();

export const twilioProtocol = getTwilioProtocol(
  window.location.hostname as Hostname
);
export const twilioHost = getTwilioHost(window.location.hostname as Hostname);
export const twilioRealm =
  getRealm(window.location.hostname as Hostname) || "prod";

export const gitSha = process.env.NX_GIT_SHA;

export const signUpUrl = `${twilioProtocol}${twilioHost}/try-twilio`;

export const loginUrl = `${twilioProtocol}${twilioHost}/login`;

export const logoutUrl = `${twilioProtocol}${twilioHost}/logout`;

export const consoleUrl = `${twilioProtocol}${twilioHost.replace(
  /www/,
  "console"
)}`;

/**
 * Asynchronously generates a deeplink for authentication flow by calling the backend endpoint.
 * Redirects the user to the resulting deeplink URI.
 *
 * @param {`/${string}`} authFlowPath - URL path to generated the deeplink for
 * @returns {Promise<void>} Promise that resolves to void once the deeplink is generated and the user is redirected.
 * @throws Will throw an error if the fetch operation fails.
 */
export const deeplink = async (
  authFlowPath: `/${string}`,
  deepLinkURL?: string
): Promise<void> => {
  const deeplinkPath =
    deepLinkURL || `${CONSOLE_ZEN_REDIRECT_ROOT_PATH}/${window.location}`;
  const authFlowUrl = new URL(authFlowPath, baseUrl);
  const fetchDeeplinkUrl = `${API_ENDPOINTS.CNB_GENERAL_RPC}/public?name=deeplinkToken&args=%7B"url"%3A"${deeplinkPath}"%7D`;

  try {
    const fetchDeeplinkQuery = await fetch(fetchDeeplinkUrl, {
      credentials: "include",
    });

    if (fetchDeeplinkQuery?.ok && fetchDeeplinkQuery?.status === 200) {
      // eslint-disable-next-line no-unsafe-optional-chaining
      const { result } = await fetchDeeplinkQuery?.json();
      authFlowUrl.search = result?.queryString;
    }
  } catch (err: any) {
    // on error, pass-through and send to auth flow path without a deeplink query param
    // eslint-disable-next-line no-console
    logger.error(err);
  }

  return window.location.assign(authFlowUrl.toString());
};

/**
 * Redirect the user to Help Center after authentication.
 *
 * @param path - Path to redirect to after authentication.
 */
export const postAuthRedirect = async ({
  authLink,
  path = "/",
  hostname = window.location.hostname,
}: PostAuthRedirectInput): Promise<void> => {
  const deepLinkPath = `/post-auth-redirect/${hostname}${path}`;
  await deeplink(authLink, deepLinkPath);
};

/**
 * Redirect the user to Help Center after login.
 *
 * @param path - Path to redirect to after login
 */
export const loginRedirectToOsc = async (path?: string): Promise<void> => {
  await postAuthRedirect({
    authLink: "/login",
    path,
    hostname: hostName,
  });
};
